import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowRight, X, Share2 } from "lucide-react";
import ShareCard from "@/components/ShareCard";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

function AnimatedNumber({ value }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!value) return setDisplay(0);
    const start = display;
    const diff = value - start;
    const duration = 1200;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + diff * ease));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span>{display.toLocaleString()}</span>;
}

export default function SobrietyCounter() {
  const [stats, setStats] = useState({ total_people: 0, total_days: 0, states_count: 0, recent: [] });
  const [open, setOpen] = useState(false);
  const [sharePledge, setSharePledge] = useState(null);
  const [form, setForm] = useState({ name: "", state: "", sobriety_date: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [justPledged, setJustPledged] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/sobriety/stats"); setStats(data); }
    catch (e) { console.error("[sobriety-counter] failed to load stats", e); }
  };
  useEffect(() => {
    load();
    setJustPledged(localStorage.getItem("sober_pledged") === "1");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.sobriety_date) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/sobriety/pledge", form);
      localStorage.setItem("sober_pledged", "1");
      setJustPledged(true);
      setOpen(false);
      setForm({ name: "", state: "", sobriety_date: "", note: "" });
      toast.success(`${data.days} days. You are seen. Welcome to the wall.`);
      load();
      // Auto-open share card so they can immediately share their moment
      setTimeout(() => setSharePledge({
        name: data.name, state: data.state, days: data.days, note: data.note,
      }), 600);
    } catch (err) {
      console.error("[sobriety-counter] pledge submit failed", err);
      toast.error("Something went wrong — try again?");
    }
    setSubmitting(false);
  };

  return (
    <section data-testid="sober-counter-section" className="relative overflow-hidden" style={{ background: "var(--forest)" }}>
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 text-white">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          {/* LEFT: numbers */}
          <div className="md:col-span-7">
            <div className="overline text-[var(--sand)] mb-5">The wall of hope</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.95] mb-8">
              Together we have stayed sober for
            </h2>

            <div className="grid grid-cols-3 gap-6 md:gap-10 mb-10">
              <div>
                <div data-testid="counter-total-days" className="font-serif-display text-5xl md:text-7xl text-[var(--sand)] leading-none">
                  <AnimatedNumber value={stats.total_days} />
                </div>
                <div className="text-xs font-mono-acc tracking-[0.2em] text-white/60 mt-3 uppercase">Days, combined</div>
              </div>
              <div>
                <div data-testid="counter-total-people" className="font-serif-display text-5xl md:text-7xl text-white leading-none">
                  <AnimatedNumber value={stats.total_people} />
                </div>
                <div className="text-xs font-mono-acc tracking-[0.2em] text-white/60 mt-3 uppercase">People</div>
              </div>
              <div>
                <div data-testid="counter-states-count" className="font-serif-display text-5xl md:text-7xl text-[var(--forest)] leading-none">
                  <AnimatedNumber value={stats.states_count} />
                </div>
                <div className="text-xs font-mono-acc tracking-[0.2em] text-white/60 mt-3 uppercase">/ 50 states</div>
              </div>
            </div>

            <button data-testid="counter-pledge-btn"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 bg-[var(--terracotta)] text-[var(--ink)] px-7 py-4 rounded-full text-sm font-medium hover:bg-[var(--terracotta-dark)] transition-all hover:-translate-y-0.5">
              <Sparkles className="w-4 h-4" />
              {justPledged ? "Add another day on the wall" : "I'm sober today — add me"}
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-white/60 text-xs mt-4">Anonymous-friendly. No address. No phone. Just a first name and the day you got clean.</p>
          </div>

          {/* RIGHT: recent pledges ticker */}
          <div className="md:col-span-5">
            <div className="rounded-sm border border-white/15 bg-white/5 backdrop-blur-md p-6">
              <div className="overline text-[var(--sand)] mb-4">Latest on the wall</div>
              {stats.recent.length === 0 ? (
                <p className="text-white/60 text-sm">Be the first.</p>
              ) : (
                <ul className="space-y-3 max-h-[360px] overflow-y-auto pr-2">
                  {stats.recent.map((p, i) => (
                    <li key={`${p.created_at || ""}-${p.name}-${i}`} className="group flex items-baseline justify-between gap-3 border-b border-white/10 pb-3 last:border-0">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{p.name}{p.state && <span className="text-white/50 text-xs ml-1">· {p.state}</span>}</div>
                        {p.note && <div className="text-xs text-white/60 mt-0.5 line-clamp-2">{p.note}</div>}
                      </div>
                      <button data-testid={`share-${i}`} onClick={() => setSharePledge(p)}
                        className="opacity-0 group-hover:opacity-100 text-white/60 hover:text-[var(--sand)] transition" aria-label="Share story">
                        <Share2 className="w-4 h-4" />
                      </button>
                      <div className="font-serif-display text-2xl text-[var(--sand)] shrink-0">{p.days}<span className="text-xs ml-1 text-white/50 font-sans">d</span></div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pledge modal */}
      {open && (
        <div data-testid="pledge-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-[var(--bg)] text-[var(--ink)] rounded-sm w-full max-w-md p-8 relative">
            <button data-testid="pledge-close" onClick={() => setOpen(false)} className="absolute top-4 right-4 text-[var(--ink-soft)] hover:text-[var(--ink)]">
              <X className="w-5 h-5" />
            </button>
            <div className="overline mb-3">Add yourself</div>
            <h3 className="font-serif-display text-3xl mb-2">You count.</h3>
            <p className="text-sm text-[var(--ink-soft)] mb-6">Every name on this wall is one more reason for the next person to believe sobriety is possible.</p>
            <form onSubmit={submit} className="space-y-3">
              <input data-testid="pledge-name" required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="First name (or initials)"
                className="w-full px-4 py-3 border border-[var(--line)] rounded-sm text-sm bg-white focus:outline-none focus:border-[var(--terracotta)]" />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="pledge-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                  className="px-4 py-3 border border-[var(--line)] rounded-sm text-sm bg-white">
                  <option value="">State (optional)</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input data-testid="pledge-date" required type="date" max={new Date().toISOString().slice(0, 10)} value={form.sobriety_date} onChange={(e) => setForm({ ...form, sobriety_date: e.target.value })}
                  className="px-4 py-3 border border-[var(--line)] rounded-sm text-sm bg-white" />
              </div>
              <textarea data-testid="pledge-note" maxLength={300} rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Optional — a short note for anyone else reading the wall"
                className="w-full px-4 py-3 border border-[var(--line)] rounded-sm text-sm bg-white focus:outline-none focus:border-[var(--terracotta)]" />
              <button data-testid="pledge-submit" disabled={submitting}
                className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-3 rounded-sm text-sm hover:bg-[var(--terracotta-dark)] disabled:opacity-50">
                {submitting ? "Adding…" : "Put me on the wall"}
              </button>
            </form>
          </div>
        </div>
      )}

      {sharePledge && <ShareCard pledge={sharePledge} onClose={() => setSharePledge(null)} />}
    </section>
  );
}
